import { Injectable, computed, signal } from '@angular/core';
import {
  AssistDecision,
  AssistExecuteResponse,
  ResolveIntentResponse,
} from './assist.model';
import { assistantSafeOutcomeReference } from '../account-identifier-presentation';

/**
 * AssistThreadService — the conversational session log behind the shell assist panel
 * (Chatbot/Assistant SPA lane, 2026-07-05).
 *
 * ADR-0039 D7 discipline: this is a READ projection of durable command outcomes — a
 * session log, never a bubble-chat transcript and never the primary loop. The command
 * box stays the primary surface (D1); the thread makes the assistant's honest state
 * journey VISIBLE: thinking → answered / partial / prepared-awaiting-confirm → executed /
 * prefilled-in-form / denied / cancelled / clarify / error. Prepare-not-execute becomes
 * inspectable history instead of a transient modal.
 *
 * Turn text is either the user's typed command or the SERVER-BUILT summary — never LLM
 * prose (ADR-0039 invariant 5). No raw model tokens are retained here.
 */

export type AssistTurnState =
  | 'thinking'   // submitted; resolve in flight
  | 'answered'   // read/help/guide/insights/query answer rendered in the live region
  | 'partial'    // an answer was rendered, but a constraint the user asked for was not applied
  | 'clarify'    // server asked for a rephrase (honest non-answer)
  | 'prepared'   // ACTION resolved; server-built description shown; awaiting the user's confirm
  | 'prefilled'  // user chose the prepare path; the REAL form/admin surface finishes it
  | 'executed'   // nav/read kind executed via OBO with status=ok (never money/entitlements)
  | 'denied'     // hard DENY from the entitlement engine — shown as the user's own limit
  | 'cancelled'  // user cancelled (or handed back to rephrase)
  | 'error';     // transport failure / rate limit / service unavailable

export type AssistTurnErrorKind = 'rate-limited' | 'unavailable' | 'generic';

export interface AssistTurn {
  readonly id: string;
  /** The user's typed command (or the server summary when synthesized). */
  readonly command: string;
  /** ISO timestamp of submission. */
  readonly at: string;
  readonly state: AssistTurnState;
  /** Wall-clock resolve latency; null while thinking or after a transport failure. */
  readonly resolveMs: number | null;
  /** True when resolution was effectively instant (deterministic/instant path). */
  readonly instant: boolean;
  readonly response: ResolveIntentResponse | null;
  readonly decision: AssistDecision | null;
  readonly executeResponse: AssistExecuteResponse | null;
  readonly errorKind: AssistTurnErrorKind | null;
  /** Short human caption for the log row (server-derived; never LLM prose). */
  readonly detail: string | null;
}

/** Resolutions faster than this read as the deterministic "instant" path (⚡). */
const INSTANT_THRESHOLD_MS = 400;

/** Session-log cap — old turns fall off; this is a log, not an archive. */
const MAX_TURNS = 24;

/** After this many seconds of thinking, the UI shows the honest "can take a while" hint. */
export const THINKING_SLOW_HINT_SECONDS = 8;

function newTurnId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `turn-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

@Injectable({ providedIn: 'root' })
export class AssistThreadService {
  private readonly _turns = signal<AssistTurn[]>([]);
  private readonly _activeTurnId = signal<string | null>(null);
  private readonly _thinkingSeconds = signal(0);
  private thinkingTimer: ReturnType<typeof setInterval> | null = null;
  private readonly turnIdByResponse = new WeakMap<ResolveIntentResponse, string>();

  /** Newest first — the panel lists the log directly under the live result. */
  readonly turns = this._turns.asReadonly();
  readonly activeTurnId = this._activeTurnId.asReadonly();
  /** Elapsed whole seconds the active turn has been thinking (0 when idle). */
  readonly thinkingSeconds = this._thinkingSeconds.asReadonly();

  /** The in-flight turn, if any (drives the live thinking affordance). */
  readonly thinkingTurn = computed<AssistTurn | null>(() => {
    const id = this._activeTurnId();
    if (!id) return null;
    const turn = this._turns().find((t) => t.id === id);
    return turn && turn.state === 'thinking' ? turn : null;
  });

  /** Completed (non-thinking) turns for the session-log listing. */
  readonly completedTurns = computed<AssistTurn[]>(() =>
    this._turns().filter((t) => t.state !== 'thinking'),
  );

  /** A command was submitted — open a thinking turn. Returns the turn id. */
  beginTurn(command: string): string {
    const id = newTurnId();
    const turn: AssistTurn = {
      id,
      command: command.trim(),
      at: new Date().toISOString(),
      state: 'thinking',
      resolveMs: null,
      instant: false,
      response: null,
      decision: null,
      executeResponse: null,
      errorKind: null,
      detail: null,
    };
    this._turns.update((turns) => [turn, ...turns].slice(0, MAX_TURNS));
    this._activeTurnId.set(id);
    this.startThinkingClock();
    return id;
  }

  /**
   * The resolve round-trip finished. `state` is the shell's classification of the
   * response (answered / clarify / prepared / denied) — the shell owns routing, the
   * thread only records it.
   */
  resolveTurn(
    id: string,
    response: ResolveIntentResponse,
    state: Exclude<AssistTurnState, 'thinking' | 'error'>,
    detail?: string | null,
  ): void {
    this.stopThinkingClock(id);
    this.turnIdByResponse.set(response, id);
    this.patchTurn(id, (turn) => ({
      ...turn,
      state,
      response,
      resolveMs: Date.now() - Date.parse(turn.at),
      instant: Date.now() - Date.parse(turn.at) < INSTANT_THRESHOLD_MS,
      detail: detail ?? turn.detail,
    }));
  }

  /** The resolve call failed (transport/429/503) — record an honest error turn. */
  failTurn(id: string, errorKind: AssistTurnErrorKind, detail?: string | null): void {
    this.stopThinkingClock(id);
    this.patchTurn(id, (turn) => ({
      ...turn,
      state: 'error',
      errorKind,
      detail: detail ?? null,
    }));
  }

  /**
   * A modal decision landed for a response we recorded. Maps the decision onto the
   * turn's terminal state. showMeHow/rephrase CANCELLED echoes must be filtered by
   * the caller (they are hand-offs, not decisions).
   */
  recordDecision(decision: AssistDecision): void {
    const id = this.turnIdByResponse.get(decision.response);
    if (!id) return;
    if (decision.choice !== 'INSTANT') {
      this.patchTurn(id, (turn) => ({
        ...turn,
        state: 'cancelled',
        decision,
        detail: 'Cancelled — nothing happened',
      }));
      return;
    }
    switch (decision.routeKind) {
      case 'MONEY_PREFILL':
        this.patchTurn(id, (turn) => ({
          ...turn,
          state: 'prefilled',
          decision,
          detail: 'Prepared in the payment form — review and submit there',
        }));
        return;
      case 'ENTITLEMENT_MODAL':
        this.patchTurn(id, (turn) => ({
          ...turn,
          state: 'prefilled',
          decision,
          detail: 'Prepared in the admin surface — you complete it there',
        }));
        return;
      case 'OBO_EXECUTE':
      default: {
        const exec = decision.executeResponse ?? null;
        this.patchTurn(id, (turn) => ({
          ...turn,
          state: 'executed',
          decision,
          executeResponse: exec,
          detail: exec?.outcomeRef
            ? `Done — ref ${assistantSafeOutcomeReference(exec.outcomeRef)}`
            : 'Done',
        }));
        return;
      }
    }
  }

  /** Mark a turn by its response object (used for show-me-how / rephrase hand-offs). */
  markByResponse(
    response: ResolveIntentResponse,
    state: Exclude<AssistTurnState, 'thinking'>,
    detail?: string | null,
  ): void {
    const id = this.turnIdByResponse.get(response);
    if (!id) return;
    this.patchTurn(id, (turn) => ({ ...turn, state, detail: detail ?? turn.detail }));
  }

  clear(): void {
    this.stopThinkingClock(null);
    this._turns.set([]);
    this._activeTurnId.set(null);
  }

  private patchTurn(id: string, patch: (turn: AssistTurn) => AssistTurn): void {
    this._turns.update((turns) => turns.map((turn) => (turn.id === id ? patch(turn) : turn)));
  }

  private startThinkingClock(): void {
    this.stopThinkingClock(null);
    this._thinkingSeconds.set(0);
    this.thinkingTimer = setInterval(() => {
      this._thinkingSeconds.update((s) => s + 1);
    }, 1000);
  }

  /** Stops the clock when the given turn is (or null forces stop) the active one. */
  private stopThinkingClock(id: string | null): void {
    if (id !== null && this._activeTurnId() !== id) return;
    if (this.thinkingTimer !== null) {
      clearInterval(this.thinkingTimer);
      this.thinkingTimer = null;
    }
    this._thinkingSeconds.set(0);
    if (id !== null) this._activeTurnId.set(null);
  }
}
